import React from 'react';
import { Container, Row, Col, Image, Table } from 'react-bootstrap';

const recognizedParams = [
  {
    key: 'title',
    label: 'Title',
    exampleValue: 'Hello%20World',
    description: 'Sets the big title text on the screen.',
    usageExample: '?title=Hello%20World',
  },
  {
    key: 'subtext',
    label: 'Subtext',
    exampleValue: 'Enjoy%20Your%20Day',
    description: 'Sets the smaller text under the title.',
    usageExample: '?subtext=Enjoy%20Your%20Day',
  },
  {
    key: 'site',
    label: 'Site (Background)',
    exampleValue: 'site3',
    description: 'Chooses a preset background (image or video).',
    usageExample: '?site=site3',
  },
  {
    key: 'customMedia',
    label: 'Custom Media',
    exampleValue: 'https://example.com/myvideo.mp4',
    description: 'Lets you pick your own background image or video.',
    usageExample: '?customMedia=https://example.com/myvideo.mp4',
  },
  {
    key: 'customLogo',
    label: 'Custom Logo',
    exampleValue: 'https://example.com/mylogo.png',
    description: 'Lets you pick a custom logo image.',
    usageExample: '?customLogo=https://example.com/mylogo.png',
  },
  {
    key: 'hideLogos',
    label: 'Hide Logos',
    noValue: true,
    exampleValue: '(no value needed)',
    description: 'Hides both logos on the screen.',
    usageExample: '?title=Hello&hideLogos',
  },
  {
    key: 'disableGif',
    label: 'Disable GIF',
    noValue: true,
    exampleValue: '(no value needed)',
    description: 'Stops the GIF overlay from showing at all.',
    usageExample: '?customMedia=https://example.com/bg.jpg&disableGif',
  },
  {
    key: 'customGif',
    label: 'Custom GIF',
    exampleValue: 'https://example.com/myCoolGif.gif',
    description: 'Uses a different GIF or image for the overlay.',
    usageExample: '?customGif=https://example.com/myCoolGif.gif',
  },
  {
    key: 'titleColor',
    label: 'Title Color',
    exampleValue: 'FF0000',
    description: 'Sets the title color by HEX (no # needed).',
    usageExample: '?title=Hello&titleColor=FF0000',
  },
  {
    key: 'subtextColor',
    label: 'Subtext Color',
    exampleValue: '00FF00',
    description: 'Sets the subtext color by HEX (no # needed).',
    usageExample: '?subtext=Green%20Text&subtextColor=00FF00',
  },
  {
    key: 'hideDateTime',
    label: 'Hide Date/Time',
    noValue: true,
    exampleValue: '(no value needed)',
    description: 'Hides the Date & Time from the signage preview and final display.',
    usageExample: '?hideDateTime',
  },
  {
    key: 'staticDateTime',
    label: 'Static Date/Time',
    exampleValue: '2025-04-15T14:30',
    description: 'Displays a fixed date/time (overrides local).',
    usageExample: '?staticDateTime=2025-04-15T14:30',
  },
  {
    key: 'onlyDate',
    label: 'Only Date',
    noValue: true,
    exampleValue: '(no value needed)',
    description: 'Displays only the date portion of time.',
    usageExample: '?onlyDate',
  },
  {
    key: 'onlyTime',
    label: 'Only Time',
    noValue: true,
    exampleValue: '(no value needed)',
    description: 'Displays only the time portion of date/time.',
    usageExample: '?onlyTime',
  },
  {
    key: 'showCountdown',
    label: 'Countdown Mode',
    noValue: true,
    exampleValue: '(no value needed)',
    description: 'Shows a countdown to a specified date/time.',
    usageExample: '?showCountdown&countdownDateTime=2025-05-01T12:00',
  },
  {
    key: 'countdownDateTime',
    label: 'Countdown Target',
    exampleValue: '2025-05-01T12:00',
    description: 'The target date/time used when countdown mode is on.',
    usageExample: '?showCountdown&countdownDateTime=2025-05-01T12:00',
  },
];

export default function AboutExpanded() {
  return (
    <Container className="py-5" style={{ fontFamily: 'Verdana, sans-serif' }}>
      <Row className="mb-4">
        <Col xs={12} className="text-center">
          <h1 style={{ fontWeight: 500, fontSize: '3rem' }}>about <span style={{ fontWeight: 'bold' }}>qry</span>sign <span class="phonetic">\ˈkwir-ē ˌsīn\</span></h1>
        </Col>
      </Row>

      <Row className="align-items-center mb-5">
        <Col md={4} xs={12} className="mb-4 mb-md-0 text-center">
          <Image
            src="/qryfox-logo.png"
            alt="Mascot or Logo"
            fluid
            style={{ maxWidth: '300px' }}
          />
        </Col>

        <Col md={8} xs={12}>
          <p style={{ fontSize: '1.1rem', lineHeight: '1.7' }}>
            <strong>qrysign</strong> is a flexible signage system designed for real-time display configuration
            through URL queries. It provides a frictionless way to create personalized full-screen announcements,
            countdowns, and digital signs in just seconds.
          </p>
          <p style={{ fontSize: '1.1rem', lineHeight: '1.7' }}>
            With <strong>qrysign</strong>, there's no need for user accounts or content management overhead. Simply
            construct a URL with query parameters to set your title, subtext, colors, background media, and overlays.
            The interface automatically adapts based on your parameters, and displays your content across digital kiosks,
            conference room monitors, or lobby TVs.
          </p>
          <p style={{ fontSize: '1.1rem', lineHeight: '1.7' }}>
            Ideal for time-sensitive updates, shared displays in collaborative workspaces, or facility-wide
            information systems, qrysign puts control in your hands with zero deployment complexity.
          </p>
        </Col>
      </Row>

      <Row className="mb-5">
        <Col>
          <h3>How to Use URL Parameters</h3>
          <p>Begin with a <code>?</code>, and separate each query with <code>&</code>. For example:</p>
          <p style={{ fontStyle: 'italic' }}>
            <code>?title=Lunch%20Time&subtext=Free%20Pizza&titleColor=FF6600&hideDateTime</code>
          </p>

          <Table striped bordered hover size="sm" responsive>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Example Value</th>
                <th>Description</th>
                <th>Usage Example</th>
              </tr>
            </thead>
            <tbody>
              {recognizedParams.map((param) => (
                <tr key={param.key}>
                  <td><code>{param.key}</code></td>
                  <td>{param.exampleValue}</td>
                  <td>{param.description}</td>
                  <td><code>{param.usageExample}</code></td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Col>
      </Row>
    </Container>
  );
}
